// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import requestWishlistQuote from "@bg/App/Handlers/Data/WishLists/RequestQuoteFromWishlist";
import { WishListGroupingItem } from "@bg/App/Services/WishListService";
import { linkIconStyleGenerator, pageTitlesPrintStyle } from "@bg/App/SharedStyles/GeneralStyles";
import { CustomAppState, xCommonActions } from "@bg/App/Store/Reducers";
import { getProductLineFromQtyToUpdateList } from "@bg/App/Store/Selectors/MyCustomProductListDetails/MyCustomProductListDetailsSelector";
import { xScreen } from "@bg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import siteMessage from "@insite/client-framework/SiteMessage";
import setShareListModalIsOpen from "@insite/client-framework/Store/Components/ShareListModal/Handlers/SetShareListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    canAddWishListLineToCart,
    getWishListLinesDataView,
} from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import updateWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/UpdateWishList";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addLinesToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import deleteWishList from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/DeleteWishList";
import deleteWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/DeleteWishListLines";
import exportWishList from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/ExportWishList";
import loadWishListIfNeeded from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListIfNeeded";
import loadWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListLines";
import setAllWishListLinesIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetAllWishListLinesIsSelected";
import setWishListTags from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetWishListTags";
import updateLoadWishListLinesParameter from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateLoadWishListLinesParameter";
import loadWishLists from "@insite/client-framework/Store/Pages/MyLists/Handlers/LoadWishLists";
import updateLoadParameter from "@insite/client-framework/Store/Pages/MyLists/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import {
    CartLineCollectionModel,
    CartLineModel,
    WishListLineModel,
    WishListModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ScheduleReminderModal, {
    ScheduleReminderModalStyles,
} from "@insite/content-library/Components/ScheduleReminderModal";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import WishListPrintModal, { WishListPrintModalStyles } from "@insite/content-library/Components/WishListPrintModal";
import MyListsEditListForm from "@insite/content-library/Widgets/MyLists/MyListsEditListForm";
import AddTagsModal from "@insite/content-library/Widgets/MyListsDetails/AddTagsModal";
import MyListsDetailsCopyListForm from "@insite/content-library/Widgets/MyListsDetails/MyListsDetailsCopyListForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Star from "@insite/mobius/Icons/Star";
import StarX from "@insite/mobius/Icons/StarX";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import { markForFocusLater } from "@insite/mobius/Overlay/helpers/focusManager";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

interface State {
    updateListModalIsOpen: boolean;
    deleteListModalIsOpen: boolean;
    copyListModalIsOpen: boolean;
    printAllModalIsOpen: boolean;
    addTagsModalIsOpen: boolean;
    scheduleReminderModalIsOpen: boolean;
    closeCopyListModalOnEsc: boolean;
    /* 
        XNG-Changes:
        The "Request Quote" button used to be shown initially, hidden while updating the wishlist quantity, 
        and then reshow once the update completed. This was inconsistent with the "Add to Cart" button, 
        which is simply disabled during loading. Now, the "Request Quote" button is disabled instead of hidden 
        to match the "Add to Cart" behavior and provide a consistent user experience.
        */
    showRequestQuote?: boolean;
}

const mapStateToProps = (state: CustomAppState) => ({
    wishList: getWishListState(state, state.pages.myListDetails.wishListId).value,
    wishListLinesDataView: getWishListLinesDataView(state, state.pages.myListDetails.loadWishListLinesParameter),
    wishListSettings: getSettingsCollection(state).wishListSettings,
    selectedWishListLineIds: state.pages.myListDetails.selectedWishListLineIds,
    myListsPageLink: getPageLinkByPageType(state, "MyListsPage"),
    allowEditingOfWishLists: getSettingsCollection(state).wishListSettings.allowEditingOfWishLists,
    productInfosByWishListLineId: state.pages.myListDetails.productInfosByWishListLineId,
    isUpdatingCart: state.context.isUpdatingCart,
    isLoading: !!(
        state.pages.myListDetails.wishListId && state.data.wishLists.isLoading[state.pages.myListDetails.wishListId]
    ),
    selectedGroupLines: state.commonActions["selectedGroupLines"],
    canRequestQuote: getCurrentCartState(state).value?.canRequestQuote,
    globalMessage: state.xHeader.globalMessage,
    // XNG-Changes
    productLinesQtyToUpdate: state.xProductListDetails.productLinesQtyToUpdate,
});

const mapDispatchToProps = {
    addWishListToCart,
    addLinesToCart,
    deleteWishList,
    deleteWishListLines,
    loadWishLists,
    loadWishListLines: makeHandlerChainAwaitable(loadWishListLines),
    updateLoadParameter,
    updateLoadWishListLinesParameter,
    setShareListModalIsOpen,
    setAllWishListLinesIsSelected,
    exportWishList,
    updateWishList,
    loadWishListIfNeeded,
    setWishListTags,
    requestWishlistQuote,
    setBreadcrumbActions: (items: any) => (dispatch: Dispatch<xCommonActions>) => {
        dispatch({ type: "commonActionSetter/SetAction", payload: { name: "breadcrumbActions", value: items } });
    },
};

const enum fields {
    copyButton = "copyButton",
    exportButton = "exportButton",
    printButton = "printButton",
    deleteListButton = "deleteListButton",
    removeSelectedButton = "removeSelectedButton",
    addTagsButton = "addTagsButton",
    editReminderButton = "editReminderButton",
    editListButton = "editListButton",
    addListToCartButton = "addListToCartButton",
    hideButton = "hideButton",
    shareButton = "shareButton",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.copyButton]: boolean;
        [fields.exportButton]: boolean;
        [fields.printButton]: boolean;
        [fields.deleteListButton]: boolean;
        [fields.removeSelectedButton]: boolean;
        [fields.addTagsButton]: boolean;
        [fields.editReminderButton]: boolean;
        [fields.editListButton]: boolean;
        [fields.addListToCartButton]: boolean;
        [fields.hideButton]: boolean;
        [fields.shareButton]: boolean;
    };
}

type Props = OwnProps & HasHistory & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface MyListsDetailsActionStyles {
    wrapper?: InjectableCss;
    printLink?: LinkPresentationProps;
    shareLink?: LinkPresentationProps;
    editLink?: LinkPresentationProps;
    titleWrapper?: InjectableCss;
    buttonWrapper?: InjectableCss;
    wideHidden?: HiddenProps;
    nameText?: TypographyProps;
    overflowMenu?: OverflowMenuPresentationProps;
    narrowHidden?: HiddenProps;
    printButton?: ButtonPresentationProps;
    addTagsButton?: ButtonPresentationProps;
    scheduleButton?: ButtonPresentationProps;
    editButton?: ButtonPresentationProps;
    shareButton?: ButtonPresentationProps;
    hideButton?: ButtonPresentationProps;
    requestQuote?: ButtonPresentationProps;
    addItemButton?: ButtonPresentationProps;
    removeSelectedButton?: ButtonPresentationProps;
    addListButton?: ButtonPresentationProps;
    editListModal?: ModalPresentationProps;
    deleteListModal?: TwoButtonModalStyles;
    deleteListItemsModal?: TwoButtonModalStyles;
    printListModal?: WishListPrintModalStyles;
    breadcrumbActionsWrapper?: InjectableCss;
    copyListModal?: ModalPresentationProps;
    scheduleReminderModal?: ScheduleReminderModalStyles;
    iconContainer?: InjectableCss;
    actionFavoriteClickable?: ClickablePresentationProps;
    starIcon?: IconPresentationProps;
    starXIcon?: IconPresentationProps;
    copyButton?: ButtonPresentationProps;
    exportButton?: ButtonPresentationProps;
    deleteButton?: ButtonPresentationProps;
    titleIconWrapper?: InjectableCss;
    // Xng-Change
    editHiddenWrapper?: HiddenProps;
}

export const actionStyles: MyListsDetailsActionStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    breadcrumbActionsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            z-index: 3;
            &.margin-class {
                ${xScreen("lte", "md")} {
                    margin-top: 60px !important;
                }

                ${xScreen("lte", "xs")} {
                    margin-top: 10px !important;
                }
            }
        `,
    },
    shareLink: linkIconStyleGenerator({
        icon: "Share",
        textSize: "13px",
        iconSize: 14,
        additionalTypographyCss: css`
            margin-right: 20px;
        `,
    }),
    printLink: linkIconStyleGenerator({
        icon: "Print",
        textSize: "13px",
        iconSize: 14,
    }),
    editLink: linkIconStyleGenerator({
        icon: "Create",
        textSize: "13px",
        iconSize: 14,
        additionalTypographyCss: css`
            line-height: 13px;
        `,
    }),
    titleWrapper: {
        css: css`
            display: flex;
            align-items: baseline;
            white-space: nowrap;
            max-width: 50%;
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            flex-wrap: nowrap;
            align-items: flex-start;

            nav {
                ${xScreen("lte", "sm")} {
                    margin-top: 2px;
                }
            }
        `,
    },
    wideHidden: {
        below: "lg",
        css: css`
            display: flex;
            flex-wrap: nowrap;
        `,
    },
    nameText: {
        ellipsis: true,
        css: css`
            ${pageTitlesPrintStyle()}
            margin-right: 15px;
        `,
    },
    narrowHidden: {
        above: "md",
    },
    overflowMenu: {
        buttonProps: {
            css: css`
                @media (max-width: 481px) {
                    min-height: 34px;
                    height: 34px;
                    max-width: 34px;
                }
            `,
        },
    },
    printButton: {
        css: css`
            padding: 0 15px;
            margin-right: 10px;
        `,
        variant: "secondary",
    },
    scheduleButton: {
        css: css`
            padding: 0 15px;
            margin-right: 10px;
        `,
        variant: "secondary",
    },
    editButton: {
        css: css`
            padding: 0 15px;
            margin-right: 10px;
        `,
        variant: "secondary",
    },
    shareButton: {
        css: css`
            padding: 0 15px;
            margin-right: 10px;
        `,
        variant: "secondary",
    },
    addItemButton: {
        typographyProps: {
            weight: "bold",
            css: css`
                white-space: nowrap;
            `,
        },
        css: css`
            padding: 0 15px;
            margin-right: 10px;
        `,
        variant: "secondary",
    },
    removeSelectedButton: {
        typographyProps: {
            weight: "bold",
            css: css`
                white-space: nowrap;
            `,
        },
        css: css`
            padding: 0 15px;
            margin-right: 10px;
        `,
        variant: "tertiary",
    },
    addListButton: {
        css: css`
            margin-right: 15px;
            &&&,
            &&&:disabled {
                background: ${getColor("primary")};
                color: ${getColor("common.white")};
                border: 2px solid ${getColor("primary")};
            }
            &&&:hover:not(:disabled) {
                background: ${getColor("common.white")};
                color: ${getColor("primary")};
            }
        `,
    },
    requestQuote: {
        variant: "secondary",
        css: css`
            margin-right: 15px;
        `,
    },
    editListModal: {
        sizeVariant: "small",
    },
    copyListModal: {
        sizeVariant: "small",
    },
    addTagsButton: {
        css: css`
            padding: 0 10px;
            margin-right: 10px;
            white-space: nowrap;
        `,
        variant: "secondary",
    },
    iconContainer: {
        css: css`
            width: 15px;
        `,
    },
    actionFavoriteClickable: {
        css: css`
            width: 15px;
        `,
    },
    starIcon: {
        src: Star,
        size: 15,
    },
    starXIcon: {
        src: StarX,
        size: 15,
        css: css`
            path {
                fill: ${getColor("primary")};
            }
        `,
    },
    copyButton: {
        css: css`
            padding: 0 15px;
            margin-right: 10px;
            white-space: nowrap;
        `,
        variant: "secondary",
    },
    exportButton: {
        css: css`
            padding: 0 15px;
            margin-right: 10px;
            white-space: nowrap;
        `,
        variant: "secondary",
    },
    deleteButton: {
        css: css`
            padding: 0 15px;
            margin-right: 10px;
            white-space: nowrap;
        `,
        variant: "secondary",
    },
    titleIconWrapper: {
        css: css`
            display: flex;
            column-gap: 10px;
            align-items: baseline;
            width: 100%;
        `,
    },
    editHiddenWrapper: {
        below: "lg",
        css: css`
            display: flex;
            flex-wrap: nowrap;
            padding-bottom: 10px;
        `,
    },
};

const styles = actionStyles;

class MyListsDetailsActions extends React.Component<Props, State> {
    static contextType = ToasterContext;
    declare context: React.ContextType<typeof ToasterContext>;

    constructor(props: Props) {
        super(props);
        this.state = {
            updateListModalIsOpen: false,
            printAllModalIsOpen: false,
            addTagsModalIsOpen: false,
            deleteListModalIsOpen: false,
            copyListModalIsOpen: false,
            scheduleReminderModalIsOpen: false,
            closeCopyListModalOnEsc: true,
            /* 
            XNG-Changes:
            The "Request Quote" button used to be shown initially, hidden while updating the wishlist quantity, 
            and then reshow once the update completed. This was inconsistent with the "Add to Cart" button, 
            which is simply disabled during loading. Now, the "Request Quote" button is disabled instead of hidden 
            to match the "Add to Cart" behavior and provide a consistent user experience.
            */
            showRequestQuote: false,
        };
    }

    componentDidUpdate() {
        this.addBreadcrumbActions();
        const canShowRequestQuote =
            (this.props.wishListLinesDataView.value?.length || 0) > 0 && this.props.canRequestQuote;
        /* 
        XNG-Changes:
        The "Request Quote" button used to be shown initially, hidden while updating the wishlist quantity, 
        and then reshow once the update completed. This was inconsistent with the "Add to Cart" button, 
        which is simply disabled during loading. Now, the "Request Quote" button is disabled instead of hidden 
        to match the "Add to Cart" behavior and provide a consistent user experience.
        */
        if (
            this.props.canRequestQuote &&
            this.state.showRequestQuote !== canShowRequestQuote &&
            !this.props.wishListLinesDataView.isLoading
        ) {
            this.setState({
                showRequestQuote: canShowRequestQuote,
            });
        }
    }

    componentWillUnmount() {
        this.props.setBreadcrumbActions("");
    }

    disableCloseCopyListModalOnEsc = () => {
        this.setState({ closeCopyListModalOnEsc: false });
    };

    enableCloseCopyListModalOnEsc = () => {
        this.setState({ closeCopyListModalOnEsc: true });
    };

    addBreadcrumbActions() {
        const { wishList, wishListSettings, globalMessage } = this.props;

        const showShare =
            !wishList?.isSharedList &&
            wishListSettings.allowMultipleWishLists &&
            wishListSettings.allowListSharing &&
            !wishList?.isAutogenerated;
        const showRemoveSelected =
            (wishList?.allowEdit || !wishList?.isSharedList) && this.linesSelected() && !wishList?.isAutogenerated;
        const showSchedule = wishListSettings.enableWishListReminders && !wishList?.isAutogenerated;

        const breadcrumbItems = (
            <StyledWrapper {...styles.breadcrumbActionsWrapper} className={globalMessage ? "margin-class" : ""}>
                {showShare ? (
                    <Link {...styles.shareLink} onClick={this.shareClickHandler}>
                        {translate("Share")}
                    </Link>
                ) : null}
                <Link
                    {...styles.printLink}
                    onClick={this.printOrOpenPrintAllModal}
                    data-test-selector="myListsDetails_printButton"
                >
                    {translate("Print")}
                </Link>
            </StyledWrapper>
        );
        this.props.setBreadcrumbActions(breadcrumbItems);
    }

    displayToast(message: React.ReactNode) {
        this.context.addToast({ body: message, messageType: "success" });
    }

    linesSelected() {
        const { selectedGroupLines } = this.props;
        let isAnySelected = false;
        if (selectedGroupLines) {
            Object.keys(selectedGroupLines).forEach(group => {
                if (selectedGroupLines[group].length > 0) {
                    isAnySelected = true;
                }
            });
        }
        return this.props.selectedWishListLineIds.length > 0 || isAnySelected;
    }

    enableAddToCart() {
        const lines = this.props.wishListLinesDataView.value;
        return (
            this.props.wishList?.canAddToCart &&
            !this.props.isUpdatingCart &&
            lines?.some(o => canAddWishListLineToCart(o, this.props.productInfosByWishListLineId)) &&
            this.allQuantitiesAreValid(lines)
        );
    }

    allQuantitiesAreValid(wishListLines: WishListLineModel[]): boolean {
        return wishListLines
            .filter(
                o =>
                    this.props.selectedWishListLineIds.length === 0 ||
                    this.props.selectedWishListLineIds.indexOf(o.id) > -1,
            )
            .every(o => o.qtyOrdered && parseFloat(o.qtyOrdered.toString()) > 0);
    }

    editClickHandler = () => {
        this.setState({ updateListModalIsOpen: true });
    };

    shareClickHandler = () => {
        this.props.setShareListModalIsOpen({ modalIsOpen: true, wishListId: this.props.wishList?.id });
    };

    printOrOpenPrintAllModal = () => {
        const { wishListLinesDataView } = this.props;
        if (!wishListLinesDataView.value || !wishListLinesDataView.pagination) {
            return null;
        }

        this.setState({ printAllModalIsOpen: true });
    };

    closePrintModal = () => {
        this.setState({ printAllModalIsOpen: false });
    };

    editCloseHandler = () => {
        this.setState({ updateListModalIsOpen: false });
    };

    addToCartClickHandler = (e: any) => {
        e.preventDefault();
        if (!this.props.wishList || !this.props.wishListLinesDataView.value) {
            return;
        }

        const changedSharedListLinesQuantities: Dictionary<number> = {};
        // XNG-Changes
        this.props.wishListLinesDataView.value.forEach(({ id, properties }) => {
            (JSON.parse(properties.grouping) as WishListGroupingItem[]).forEach(({ groupLineId, qtyOrdered }) => {
                const value =
                    getProductLineFromQtyToUpdateList(this.props?.productLinesQtyToUpdate, id, groupLineId)?.qty ||
                    qtyOrdered ||
                    this.props.productInfosByWishListLineId[id]!.qtyOrdered;
                if (changedSharedListLinesQuantities[id] === undefined) {
                    changedSharedListLinesQuantities[id] = value;
                } else {
                    changedSharedListLinesQuantities[id] = changedSharedListLinesQuantities[id] + value;
                }
            });
        });

        // XNG-Changes: If any line is selected, get lines from selectedGroupLines and adjust the quantities
        if (this.linesSelected()) {
            const { selectedGroupLines, wishList } = this.props;
            const newCartLinesWithAdjustedQuanities = [] as CartLineModel[];
            const groupList: string[] = JSON.parse(wishList.properties.groups);
            groupList.forEach(group => {
                if (
                    selectedGroupLines &&
                    Object.prototype.hasOwnProperty.call(selectedGroupLines, group) &&
                    selectedGroupLines[group].length > 0
                ) {
                    selectedGroupLines[group].forEach((id: string) => {
                        let line = this.props.wishListLinesDataView.value!.filter(line => line.id === id)[0];
                        const currentGroup = JSON.parse(line.properties.grouping).filter(
                            (x: any) => x.groupName === group,
                        )[0];
                        line = {
                            ...line,
                            qtyOrdered:
                                getProductLineFromQtyToUpdateList(
                                    this.props?.productLinesQtyToUpdate,
                                    id,
                                    currentGroup?.groupLineId,
                                )?.qty || currentGroup.qtyOrdered,
                            notes: currentGroup.notes,
                        };
                        if (
                            newCartLinesWithAdjustedQuanities &&
                            newCartLinesWithAdjustedQuanities.filter(
                                (cartLine: CartLineModel) => cartLine.id === line.id,
                            ).length
                        ) {
                            let itemIndex = 0;
                            const existingLine = newCartLinesWithAdjustedQuanities.find(
                                (cartLine: any, index: number) => {
                                    if (cartLine.id === line.id) {
                                        itemIndex = index;
                                        return true;
                                    }

                                    return false;
                                },
                            );

                            const combinedLine = {
                                ...existingLine,
                                qtyOrdered: line.qtyOrdered + (existingLine!.qtyOrdered || 0),
                                notes: `${existingLine!.notes} ${line.notes}`,
                            };
                            newCartLinesWithAdjustedQuanities[itemIndex] = {
                                ...(combinedLine as any as CartLineModel),
                            };
                        } else {
                            newCartLinesWithAdjustedQuanities.push(line as any as CartLineModel);
                        }
                    });
                }
            });

            this.props.addLinesToCart({
                apiParameter: {
                    cartId: API_URL_CURRENT_FRAGMENT,
                    cartLineCollection: { cartLines: newCartLinesWithAdjustedQuanities } as CartLineCollectionModel,
                },
                onSuccess: this.onAddToCartSuccess,
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        this.onSuccess?.();
                    }
                },
            });
        } else {
            this.props.addWishListToCart({
                apiParameter: {
                    wishListId: this.props.wishList.id,
                    changedSharedListLinesQuantities,
                },
                onSuccess: this.onAddToCartSuccess,
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        this.onSuccess?.(resultProps.apiResult);
                    }
                },
            });
        }
    };

    requestQuoteForWishlist = (isCustom: boolean) => {
        const { requestWishlistQuote, wishList, selectedGroupLines, wishListLinesDataView } = this.props;
        if (!wishList || !wishList.id || !wishListLinesDataView || !wishListLinesDataView.value) {
            return;
        }
        if (this.linesSelected()) {
            if (Object.keys(selectedGroupLines).filter(key => selectedGroupLines[key].length > 0)?.length > 8) {
                this.context.addToast({ body: siteMessage("Custom_MaxAllowedGroups"), messageType: "danger" });
                return;
            }
            const listGroups: string[] = JSON.parse(wishList.properties.groups);
            const groupsProductsInfo: any = {};
            listGroups.forEach(group => {
                if (
                    selectedGroupLines &&
                    Object.prototype.hasOwnProperty.call(selectedGroupLines, group) &&
                    selectedGroupLines[group].length
                ) {
                    groupsProductsInfo[group] = [];
                    selectedGroupLines[group].forEach((id: string) => {
                        const productErp = wishListLinesDataView.value!.filter(line => line.id === id)[0]?.erpNumber;
                        groupsProductsInfo[group] = [...groupsProductsInfo[group], productErp];
                    });
                }
            });
            requestWishlistQuote({
                apiParameter: {
                    wishlistId: wishList.id,
                    groupsProductsInfo,
                },
                isCustomQuote: isCustom,
            });
        } else {
            if (JSON.parse(wishList.properties.groups)?.length > 8) {
                this.context.addToast({ body: siteMessage("Custom_MaxAllowedGroups"), messageType: "danger" });
                return;
            }
            requestWishlistQuote({
                apiParameter: {
                    wishlistId: wishList.id,
                },
                isCustomQuote: isCustom,
            });
        }
    };

    onAddToCartSuccess = (apiResult?: CartLineCollectionModel) => {
        this.displayToast(
            this.linesSelected()
                ? translate("Added to Cart")
                : apiResult?.notAllAddedToCart
                  ? siteMessage("Lists_Items_Not_All_Added_To_Cart")
                  : translate("List Added to Cart"),
        );
    };

    deleteClickHandler = () => {
        this.setState({ deleteListModalIsOpen: true });
    };

    deleteCancelHandler = () => {
        this.setState({ deleteListModalIsOpen: false });
    };

    deleteSubmitHandler = () => {
        if (!this.props.wishList) {
            return;
        }

        this.setState({ deleteListModalIsOpen: false });
        this.props.myListsPageLink && this.props.history.push(this.props.myListsPageLink.url);
        this.props.deleteWishList({
            wishListId: this.props.wishList.id,
            onSuccess: this.onDeleteSuccess,
            onComplete() {
                this.onSuccess?.();
            },
        });
    };

    onDeleteSuccess = () => {
        this.displayToast(translate("List Deleted"));
    };

    copyClickHandler = () => {
        this.props.updateLoadParameter({ pageSize: 999 });
        this.props.loadWishLists();
        this.setState({ copyListModalIsOpen: true });
    };

    exportClickHandler = () => {
        if (!this.props.wishList) {
            return;
        }

        this.props.exportWishList({
            wishListId: this.props.wishList.id,
        });
    };

    copyCancelHandler = () => {
        this.setState({ copyListModalIsOpen: false });
    };

    copySubmitHandler = () => {
        this.setState({ copyListModalIsOpen: false });
    };

    scheduleReminderClickHandler = () => {
        this.setState({ scheduleReminderModalIsOpen: true });
    };

    closeScheduleReminderModalHandler = () => {
        this.setState({ scheduleReminderModalIsOpen: false });
    };

    toggleFavorite = (wishList: WishListModel) => {
        if (!wishList) {
            return;
        }

        const that = this;
        this.props.updateWishList({
            apiParameter: {
                wishList: {
                    ...wishList,
                    isFavorite: !wishList.isFavorite,
                    wishListLineCollection: null,
                },
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    that.props.loadWishListIfNeeded({ wishListId: resultProps.result?.wishList.id });
                } else if (resultProps.result?.errorMessage) {
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };

    addTagsClickHandler = () => {
        this.setState({ addTagsModalIsOpen: true });
    };

    closeAddTagsModalHandler = () => {
        this.setState({ addTagsModalIsOpen: false });
        const tags = this.props.wishList?.wishListTags?.map(o => o.tag);
        if (tags) {
            this.props.setWishListTags({ wishListTags: tags });
        }
    };

    toggleHide = (wishList: WishListModel) => {
        if (!wishList) {
            return;
        }

        const that = this;
        this.props.updateWishList({
            apiParameter: {
                wishList: {
                    ...wishList,
                    isHidden: !wishList.isHidden,
                    wishListLineCollection: null,
                },
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    that.props.loadWishListIfNeeded({ wishListId: resultProps.result?.wishList.id });
                } else if (resultProps.result?.errorMessage) {
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };

    render() {
        const {
            wishList,
            wishListSettings,
            wishListLinesDataView,
            allowEditingOfWishLists,
            canRequestQuote,
            isLoading,
        } = this.props;
        if (!wishList) {
            return null;
        }

        const showEdit = !wishList.isSharedList && !wishList.isAutogenerated;
        const showSchedule = wishListSettings.enableWishListReminders;
        const showAddToCart = (wishListLinesDataView.value?.length || 0) > 0;
        /* 
        </XNG-Changes>:
        The "Request Quote" button used to be shown initially, hidden while updating the wishlist quantity, 
        and then reshow once the update completed. This was inconsistent with the "Add to Cart" button, 
        which is simply disabled during loading. Now, the "Request Quote" button is disabled instead of hidden 
        to match the "Add to Cart" behavior and provide a consistent user experience.
        */
        const showRequestQuote = this.state.showRequestQuote;
        // const showRequestQuote = (wishListLinesDataView.value?.length || 0) > 0 && canRequestQuote;
        // </XNG-Changes>
        const showCopy = wishListSettings.allowMultipleWishLists && showAddToCart;
        // const showRemoveSelected =
        //     (wishList.allowEdit || !wishList.isSharedList) && this.linesSelected() && !wishList.isAutogenerated;
        const addListToCartButtonText = this.linesSelected()
            ? translate("Add Selected to Cart")
            : translate("Add List to Cart");
        const requestQuoteButtonText = this.linesSelected()
            ? translate("Request Quote For Selected")
            : translate("Request Quote");
        const scheduleButtonText = wishList.schedule ? translate("Edit Reminder") : translate("Schedule Reminder");
        const showDelete = allowEditingOfWishLists && !wishList.isSharedList && !wishList.isAutogenerated;
        const showCustomQuote = wishList.properties?.allowConvertToCustomQuote;

        const toggleHideButtonText = wishList.isHidden ? translate("Unhide List") : translate("Hide List");
        const toggleFavoriteLabel = wishList.isFavorite
            ? translate("Remove from favorites")
            : translate("Add to favorites");

        const overflowItems = [];
        const wideHiddenOverflowMenu = [];
        const wideHiddenButtons = [];

        // Xng-Change
        if (showCustomQuote) {
            overflowItems.push(
                <Clickable onClick={() => this.requestQuoteForWishlist(true)} key="my-customer-quotes">
                    {translate("My Customer Quotes")}
                    {this.linesSelected() ? " For Selected" : null}
                </Clickable>,
            );
        }

        if (showRequestQuote) {
            wideHiddenButtons.push(
                <Button
                    {...styles.requestQuote}
                    disabled={
                        !this.enableAddToCart() ||
                        !showRequestQuote ||
                        /* 
                        XNG-Changes:
                        The "Request Quote" button used to be shown initially, hidden while updating the wishlist quantity, 
                        and then reshow once the update completed. This was inconsistent with the "Add to Cart" button, 
                        which is simply disabled during loading. Now, the "Request Quote" button is disabled instead of hidden 
                        to match the "Add to Cart" behavior and provide a consistent user experience.
                        */
                        !canRequestQuote ||
                        (this.props.wishListLinesDataView.value?.length || 0) <= 0
                    }
                    key="request-quote"
                    onClick={() => this.requestQuoteForWishlist(false)}
                    data-test-selector="requestQuote"
                >
                    {requestQuoteButtonText}
                </Button>,
            );
        }

        if (showCopy) {
            if (this.props.fields.copyButton === false) {
                wideHiddenButtons.push(
                    <Button
                        {...styles.copyButton}
                        key="copy"
                        onClick={this.copyClickHandler}
                        data-test-selector="copyList"
                    >
                        {translate("Copy")}
                    </Button>,
                );
            } else {
                overflowItems.push(
                    <Clickable key="copy" onClick={this.copyClickHandler} data-test-selector="copyList">
                        {translate("Copy")}
                    </Clickable>,
                );
            }
        }

        // if (this.props.fields.printButton === false) {
        //     wideHiddenButtons.push(
        //         <Button
        //             {...styles.printButton}
        //             key="print"
        //             onClick={this.printOrOpenPrintAllModal}
        //              data-test-selector="printList"
        //              disabled={(wishListLinesDataView.value?.length ?? 0) <= 0}
        //         >
        //             {translate("Print")}
        //         </Button>,
        //     );
        // } else {
        //     overflowItems.push(
        //         <Clickable
        //             key="print"
        //             onClick={this.printOrOpenPrintAllModal}
        //             data-test-selector="myListsDetails_printButton"
        //             disabled={(wishListLinesDataView.value?.length ?? 0) <= 0}
        //         >
        //             {translate("Print")}
        //         </Clickable>,
        //     );
        // }

        if (!wishList.isAutogenerated) {
            if (this.props.fields.addTagsButton) {
                overflowItems.push(
                    <Clickable key="addTags" onClick={this.addTagsClickHandler} data-test-selector="addTags">
                        {translate("Add Tags")}
                    </Clickable>,
                );
            } else {
                wideHiddenButtons.push(
                    <Button
                        {...styles.addTagsButton}
                        key="addTags"
                        onClick={this.addTagsClickHandler}
                        data-test-selector="addTags"
                    >
                        {translate("Add Tags")}
                    </Button>,
                );
            }
        }

        if (showSchedule) {
            if (this.props.fields.editReminderButton === false) {
                wideHiddenButtons.push(
                    <Button
                        {...styles.scheduleButton}
                        key="scheduleReminder"
                        onClick={this.scheduleReminderClickHandler}
                        data-test-selector="scheduleReminderList"
                    >
                        {scheduleButtonText}
                    </Button>,
                );
            } else {
                overflowItems.push(
                    <Clickable
                        key="scheduleReminder"
                        onClick={this.scheduleReminderClickHandler}
                        data-test-selector="scheduleReminderList"
                    >
                        {scheduleButtonText}
                    </Clickable>,
                );
            }
        }

        if (!wishList.isAutogenerated) {
            if (this.props.fields.hideButton === false) {
                wideHiddenButtons.push(
                    <Button
                        {...styles.hideButton}
                        key="hide"
                        onClick={() => this.toggleHide(wishList)}
                        data-test-selector="myListsDetailsHide"
                        disabled={isLoading}
                    >
                        {toggleHideButtonText}
                    </Button>,
                );
            } else {
                overflowItems.push(
                    <Clickable
                        key="hide"
                        onClick={() => this.toggleHide(wishList)}
                        data-test-selector="myListsDetailsHide"
                        disabled={isLoading}
                    >
                        {toggleHideButtonText}
                    </Clickable>,
                );
            }
        }

        // if (showEdit) {
        //     if (this.props.fields.editListButton === false) {
        //         wideHiddenButtons.push(
        //             <Button
        //                 {...styles.editButton}
        //                 key="edit"
        //                 onClick={this.editClickHandler}
        //                 data-test-selector="editList"
        //             >
        //                 {translate("Edit")}
        //             </Button>,
        //         );
        //     } else {
        //         overflowItems.push(
        //             <Clickable key="edit" onClick={this.editClickHandler} data-test-selector="editList">
        //                 {translate("Edit")}
        //             </Clickable>,
        //         );
        //     }
        // }

        // if (showRemoveSelected) {
        //     if (this.props.fields.removeSelectedButton === false) {
        //         wideHiddenButtons.push(
        //             <Button
        //                 {...styles.removeSelectedButton}
        //                 key="removeSelected"
        //                 onClick={this.removeSelectedClickHandler}
        //                 data-test-selector="myListDetails_removeSelected"
        //             >
        //                 {translate("Remove Selected")}
        //             </Button>,
        //         );
        //     } else {
        //         overflowItems.push(
        //             <Clickable
        //                 key="removeSelected"
        //                 onClick={this.removeSelectedClickHandler}
        //                 data-test-selector="myListDetails_removeSelected"
        //             >
        //                 {translate("Remove Selected")}
        //             </Clickable>,
        //         );
        //     }
        // }

        if (wishList.wishListLinesCount > 0) {
            if (this.props.fields.exportButton === false) {
                wideHiddenButtons.push(
                    <Button
                        {...styles.exportButton}
                        key="export"
                        onClick={this.exportClickHandler}
                        data-test-selector="exportList"
                    >
                        {translate("Export")}
                    </Button>,
                );
            } else {
                overflowItems.push(
                    <Clickable key="export" onClick={this.exportClickHandler} data-test-selector="exportList">
                        {translate("Export")}
                    </Clickable>,
                );
            }
        }

        if (showDelete) {
            if (this.props.fields.deleteListButton === false) {
                wideHiddenButtons.push(
                    <Button
                        {...styles.deleteButton}
                        key="delete"
                        onClick={this.deleteClickHandler}
                        data-test-selector="deleteList"
                    >
                        {translate("Delete")}
                    </Button>,
                );
            } else {
                overflowItems.push(
                    <Clickable key="delete" onClick={this.deleteClickHandler} data-test-selector="deleteList">
                        {translate("Delete")}
                    </Clickable>,
                );
            }
        }

        if (this.props.fields.addListToCartButton) {
            overflowItems.push(
                <Clickable
                    key="addToCart"
                    disabled={!this.enableAddToCart() || !showAddToCart}
                    onClick={this.addToCartClickHandler}
                    data-test-selector="myListDetails_addListToCart"
                >
                    {addListToCartButtonText}
                </Clickable>,
            );
        } else {
            wideHiddenButtons.push(
                <Button
                    key="addToCart"
                    disabled={!this.enableAddToCart() || !showAddToCart}
                    {...styles.addListButton}
                    onClick={this.addToCartClickHandler}
                    data-test-selector="myListDetails_addListToCart"
                >
                    {addListToCartButtonText}
                </Button>,
            );
        }

        if (overflowItems.length > 0) {
            wideHiddenOverflowMenu.push(
                <OverflowMenu key="overflowMenu" onOpen={markForFocusLater} {...styles.overflowMenu}>
                    {overflowItems}
                </OverflowMenu>,
            );
        }

        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.titleWrapper}>
                    <StyledWrapper {...styles.titleIconWrapper}>
                        {!wishList.isAutogenerated && (
                            <StyledWrapper {...styles.iconContainer}>
                                <Clickable
                                    {...styles.actionFavoriteClickable}
                                    onClick={() => this.toggleFavorite(wishList)}
                                    data-test-selector="myListsDetailsFavorite"
                                    disabled={isLoading}
                                >
                                    {wishList.isFavorite && (
                                        <Icon
                                            {...styles.starXIcon}
                                            data-test-selector="myListsDetailsFavoriteStarXIcon"
                                        />
                                    )}
                                    {!wishList.isFavorite && (
                                        <Icon
                                            {...styles.starIcon}
                                            data-test-selector="myListsDetailsFavoriteStarIcon"
                                        />
                                    )}
                                </Clickable>
                            </StyledWrapper>
                        )}
                        <Typography as="h1" variant="h1" {...styles.nameText} data-test-selector="listName">
                            {wishList.name}
                        </Typography>
                    </StyledWrapper>
                    <Hidden {...styles.editHiddenWrapper}>
                        {showEdit && (
                            <Link {...styles.editLink} onClick={this.editClickHandler}>
                                {translate("Edit")}
                            </Link>
                        )}
                    </Hidden>
                </StyledWrapper>
                <StyledWrapper {...styles.buttonWrapper} data-test-selector="menuWrapper">
                    <Hidden {...styles.narrowHidden}>
                        <OverflowMenu position="end" onOpen={markForFocusLater} {...styles.overflowMenu}>
                            {showEdit && <Clickable onClick={this.editClickHandler}>{translate("Edit")}</Clickable>}
                            {showAddToCart ? (
                                <Clickable onClick={this.addToCartClickHandler}>{addListToCartButtonText}</Clickable>
                            ) : null}
                            {showRequestQuote ? (
                                <Clickable onClick={() => this.requestQuoteForWishlist(false)}>
                                    {requestQuoteButtonText}
                                </Clickable>
                            ) : null}
                            {showCustomQuote ? (
                                <Clickable onClick={() => this.requestQuoteForWishlist(true)}>
                                    {translate("My Customer Quotes")}
                                    {this.linesSelected() ? " For Selected" : null}
                                </Clickable>
                            ) : null}
                            {showSchedule ? (
                                <Clickable onClick={this.scheduleReminderClickHandler}>{scheduleButtonText}</Clickable>
                            ) : null}
                            {showCopy ? (
                                <Clickable onClick={this.copyClickHandler}>{translate("Copy")}</Clickable>
                            ) : null}
                            <Clickable onClick={this.exportClickHandler}>{translate("Export")}</Clickable>
                            {showDelete ? (
                                <Clickable onClick={this.deleteClickHandler}>{translate("Delete")}</Clickable>
                            ) : null}
                            {!wishList.isAutogenerated && (
                                <Clickable onClick={() => this.toggleHide(wishList)} disabled={isLoading}>
                                    {toggleHideButtonText}
                                </Clickable>
                            )}
                            {!wishList.isAutogenerated && (
                                <Clickable onClick={this.addTagsClickHandler}>{translate("Add Tags")}</Clickable>
                            )}
                            <Clickable onClick={this.printOrOpenPrintAllModal}>{translate("Print")}</Clickable>
                            {wishList.wishListLinesCount > 0 ? (
                                <Clickable onClick={this.exportClickHandler}>{translate("Export")}</Clickable>
                            ) : null}
                        </OverflowMenu>
                    </Hidden>
                    <Hidden {...styles.wideHidden} data-test-selector="wideHidden">
                        {wideHiddenButtons}
                        {wideHiddenOverflowMenu}
                    </Hidden>
                </StyledWrapper>

                <Modal
                    headline={translate("Edit List Detail")}
                    {...styles.editListModal}
                    isOpen={this.state.updateListModalIsOpen}
                    handleClose={this.editCloseHandler}
                >
                    <MyListsEditListForm
                        wishList={wishList}
                        onCancel={this.editCloseHandler}
                        onSubmit={this.editCloseHandler}
                    ></MyListsEditListForm>
                </Modal>
                <TwoButtonModal
                    headlineText={translate("Delete List")}
                    {...styles.deleteListModal}
                    modalIsOpen={this.state.deleteListModalIsOpen}
                    messageText={`${translate("Are you sure you want to delete")} ${wishList.name}?`}
                    cancelButtonText={translate("Cancel")}
                    submitButtonText={translate("Delete")}
                    onCancel={this.deleteCancelHandler}
                    onSubmit={this.deleteSubmitHandler}
                    submitTestSelector="submitDeleteList"
                ></TwoButtonModal>
                {wishListLinesDataView.value && (
                    <WishListPrintModal
                        isOpen={this.state.printAllModalIsOpen}
                        handleClose={this.closePrintModal}
                        updateParameterFunction={(pageSize: number) => {
                            if (wishListLinesDataView.pagination?.pageSize === pageSize) {
                                return;
                            }

                            this.props.updateLoadWishListLinesParameter({ wishListId: wishList.id, pageSize });
                        }}
                        awaitableLoader={this.props.loadWishListLines}
                        initialPageSize={
                            wishListLinesDataView.value ? wishListLinesDataView.pagination?.pageSize || 8 : 8
                        }
                        reloading={false}
                        lineCollection={{
                            pagination: wishListLinesDataView.value ? wishListLinesDataView?.pagination || null : null,
                        }}
                        styles={styles.printListModal}
                        selectedIds={this.props.selectedWishListLineIds}
                    />
                )}
                <Modal
                    headline={translate("Copy List")}
                    isOpen={this.state.copyListModalIsOpen}
                    handleClose={this.copyCancelHandler}
                    closeOnEsc={this.state.closeCopyListModalOnEsc}
                    {...styles.copyListModal}
                >
                    <MyListsDetailsCopyListForm
                        onCancel={this.copyCancelHandler}
                        onSubmit={this.copySubmitHandler}
                        enableCloseCopyListModalOnEsc={this.enableCloseCopyListModalOnEsc}
                        disableCloseCopyListModalOnEsc={this.disableCloseCopyListModalOnEsc}
                    ></MyListsDetailsCopyListForm>
                </Modal>
                <ScheduleReminderModal
                    wishList={wishList}
                    isOpen={this.state.scheduleReminderModalIsOpen}
                    handleClose={this.closeScheduleReminderModalHandler}
                    extendedStyles={styles.scheduleReminderModal}
                />
                <AddTagsModal isOpen={this.state.addTagsModalIsOpen} handleClose={this.closeAddTagsModalHandler} />
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(MyListsDetailsActions)),
    definition: {
        group: "My Lists Details",
        displayName: "Actions",
        allowedContexts: ["MyListsDetailsPage"],
        fieldDefinitions: [
            {
                name: "overflowButtonsTitle",
                displayName: "Overflow Buttons",
                editorTemplate: "LabelField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: "overflowButtonsHorizontalRule",
                defaultValue: "",
                editorTemplate: "HorizontalRule",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.copyButton,
                displayName: "Copy",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 2,
            },
            {
                name: fields.exportButton,
                displayName: "Export",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 3,
            },
            {
                name: fields.printButton,
                displayName: "Print",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 4,
            },
            {
                name: fields.deleteListButton,
                displayName: "Delete List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 5,
            },
            {
                name: fields.addTagsButton,
                displayName: "Add Tags",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 6,
            },
            {
                name: fields.editReminderButton,
                displayName: "Edit Reminder",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 7,
            },
            {
                name: fields.editListButton,
                displayName: "Edit List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 8,
            },
            {
                name: fields.addListToCartButton,
                displayName: "Add List To Cart",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 9,
            },
            {
                name: fields.hideButton,
                displayName: "Hide",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 10,
            },
            {
                name: fields.shareButton,
                displayName: "Share",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 11,
            },
            {
                name: fields.removeSelectedButton,
                displayName: "Remove Selected",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 12,
            },
        ],
    },
};

export default widgetModule;
